import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, ButtonGroup, Card, Form, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import Footer from "../../components/comunes/Footer";
import TituloPagina from "../../components/comunes/TituloPagina";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { canManageOperacionesImportacion, getRoleFromToken } from "../../utils/roleAccess";
import { importacionOperacionesApi } from "../importacion-operaciones/importacionOperacionesApi";
import "../importacion-operaciones/importacion-operaciones.css";

const formatCurrency = (value) => {
  const numericValue = Number(value || 0);
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(Number.isFinite(numericValue) ? numericValue : 0);
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const textValue = String(value);
  const isoMatch = textValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? textValue : date.toLocaleDateString("es-AR");
};

const formatPercent = (value) => `${Number(value || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const formatListPreview = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "-";
  return items.slice(0, 3).join(", ");
};

const conciliationFilters = ["TODOS", "MATCH", "MATCH_PROBABLE", "SOLO_FISERV", "SOLO_CLOVER", "CONFLICTO"];

const getConciliationBadgeVariant = (status) => {
  if (status === "MATCH") return "success";
  if (status === "MATCH_PROBABLE") return "info";
  if (status === "CONFLICTO") return "danger";
  if (status === "SOLO_FISERV" || status === "SOLO_CLOVER") return "warning";
  return "secondary";
};

const getOperationValue = (operation, key) => {
  const value = operation?.[key];
  if (value === null || value === undefined || value === "") return "-";
  if (key === "amount" || key === "refundAmount" || key === "cashWithdrawalAmount") return formatCurrency(value);
  if (key === "operationDate") return formatDateOnly(value);
  return String(value);
};

const getResultStatus = (item) => item?.estadoActual || item?.status || "-";
const getResultAutomaticStatus = (item) => item?.estadoAutomatico || item?.status || "-";
const getClassification = (item) => item?.clasificacion || {};

const getClassificationBadgeVariant = (value) => {
  if (value === "CREDITO") return "primary";
  if (value === "DEBITO") return "success";
  if (value === "DEVOLUCION" || value === "ANULADO") return "danger";
  if (value === "PENDIENTE" || value === "LIQUIDADO_POSTERIORMENTE") return "warning";
  return "secondary";
};

const getEvidenceText = (evidence) => {
  if (!evidence) return "-";
  const matches = [];
  if (evidence.terminal) matches.push("terminal");
  if (evidence.date) matches.push("fecha");
  if (evidence.amount) matches.push("importe");
  if (evidence.cardLast4) matches.push("tarjeta");
  if (evidence.authorization) matches.push("autorizacion");
  if (evidence.batch) matches.push("lote");
  if (evidence.receipt) matches.push("cupon/recibo");
  if (evidence.brand) matches.push("marca");
  if (evidence.installments) matches.push("cuotas");
  if (evidence.timeNear) matches.push("hora cercana");
  const conflicts = Array.isArray(evidence.contradictions) && evidence.contradictions.length
    ? ` Contradicciones: ${evidence.contradictions.join(", ")}.`
    : "";
  return `${matches.length ? matches.join(", ") : "sin senales fuertes."}${conflicts}`;
};

const getMappingDisplayStatus = (estado) => {
  if (estado === "MATCH" || estado === "DUDOSO") return estado;
  return "SIN_MAPEO";
};

const getMappingBadgeVariant = (estado) => {
  const displayStatus = getMappingDisplayStatus(estado);
  if (displayStatus === "MATCH") return "success";
  if (displayStatus === "DUDOSO") return "warning";
  return "danger";
};

const getCoverageBadgeVariant = (estado) => {
  if (estado === "DISPONIBLE") return "success";
  if (estado === "PROCESANDO") return "info";
  if (estado === "FALTANTE") return "warning";
  if (estado === "ERROR") return "danger";
  return "secondary";
};

const getNombreCrm = (alta) => {
  if (!alta) return "-";
  return alta.nombreDeFantasia || alta.razonSocial || "-";
};

const ConciliacionOperaciones = () => {
  const navigate = useNavigate();
  const { darkMode } = React.useContext(DarkModeContext);
  const [fechaLiquidacion, setFechaLiquidacion] = useState("");
  const [diasBusqueda, setDiasBusqueda] = useState(20);
  const [archivoZip, setArchivoZip] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [solicitandoClover, setSolicitandoClover] = useState(false);
  const [conciliando, setConciliando] = useState(false);
  const [pollingCobertura, setPollingCobertura] = useState(false);
  const [resultadoZip, setResultadoZip] = useState(null);
  const [resultadoClover, setResultadoClover] = useState(null);
  const [resultadoConciliacion, setResultadoConciliacion] = useState(null);
  const [historialConciliacion, setHistorialConciliacion] = useState(null);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [filtroConciliacion, setFiltroConciliacion] = useState("TODOS");
  const [filtrosDetalle, setFiltrosDetalle] = useState({
    terminal: "",
    comercio: "",
    fechaOperacion: "",
    importe: "",
    tarjetaUltimos4: "",
    autorizacion: "",
    merchantId: "",
    tipoMedio: "",
    estadoFinanciero: "",
    confianzaClasificacion: "",
    soloPendientesRevision: false,
  });
  const [detalleConciliacion, setDetalleConciliacion] = useState(null);

  const token = getToken();
  const role = useMemo(() => getRoleFromToken(token), [token]);
  const reportesFaltantes = useMemo(
    () => resultadoZip?.diagnosticoMapeoClover?.reportesFaltantes || [],
    [resultadoZip]
  );
  const reportesSolicitablesClover = useMemo(() => {
    if (reportesFaltantes.length) return reportesFaltantes;

    const items = resultadoZip?.diagnosticoMapeoClover?.items || [];
    return items
      .filter((item) =>
        item.estado === "MATCH" &&
        item.coverage?.estadoUi === "ERROR" &&
        item.cloverCandidate?.dashboardMerchantId &&
        item.fechaOperacion
      )
      .map((item) => ({
        merchantId: item.cloverCandidate.dashboardMerchantId,
        accountKey: item.cloverCandidate.accountKey,
        commerceName: item.cloverCandidate.commerceName,
        date: item.fechaOperacion,
        terminalFiserv: item.terminalFiserv,
      }));
  }, [reportesFaltantes, resultadoZip]);
  const reportesDisponibles = useMemo(
    () => resultadoZip?.diagnosticoMapeoClover?.resumen?.reportesDisponibles || 0,
    [resultadoZip]
  );
  const metricasClasificacion = resultadoConciliacion?.metricasClasificacion || null;
  const resumenPorComercio = resultadoConciliacion?.resumenPorComercio || [];
  const resultadosConciliacionFiltrados = useMemo(() => {
    const items = resultadoConciliacion?.resultados || [];
    if (filtroConciliacion === "TODOS") return items;
    return items.filter((item) => getResultStatus(item) === filtroConciliacion);
  }, [filtroConciliacion, resultadoConciliacion]);

  React.useEffect(() => {
    if (!token || !canManageOperacionesImportacion(role)) {
      navigate("/");
    }
  }, [navigate, role, token]);

  const cargarHistorialConciliacion = React.useCallback(async () => {
    try {
      setCargandoHistorial(true);
      const data = await importacionOperacionesApi.obtenerHistorialConciliacion({ take: 10 }, token);
      setHistorialConciliacion(data);
    } catch (error) {
      setHistorialConciliacion({ runs: [], metricas: null, error: error.message });
    } finally {
      setCargandoHistorial(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token && canManageOperacionesImportacion(role)) {
      cargarHistorialConciliacion();
    }
  }, [cargarHistorialConciliacion, role, token]);

  React.useEffect(() => {
    if (!pollingCobertura || !fechaLiquidacion) {
      return undefined;
    }

    let attempts = 0;
    let stopped = false;
    const refresh = async () => {
      attempts += 1;
      try {
        const data = await importacionOperacionesApi.obtenerDiagnosticoMapeoClover({ fechaLiquidacion }, token);
        const diagnostico = data?.diagnosticoMapeoClover;
        if (!diagnostico) {
          return;
        }
        setResultadoZip((prev) => prev ? { ...prev, diagnosticoMapeoClover: diagnostico } : prev);
        const sigueProcesando = diagnostico.items?.some((item) => item.coverage?.estadoUi === "PROCESANDO");
        if (!sigueProcesando || attempts >= 30) {
          stopped = true;
          setPollingCobertura(false);
        }
      } catch (error) {
        if (attempts >= 3) {
          stopped = true;
          setPollingCobertura(false);
        }
      }
    };

    const intervalId = window.setInterval(() => {
      if (!stopped) {
        refresh();
      }
    }, 8000);

    refresh();
    return () => window.clearInterval(intervalId);
  }, [fechaLiquidacion, pollingCobertura, token]);

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("archivo", archivoZip);
    formData.append("diasBusqueda", String(diasBusqueda || 20));
    formData.append("token", token || "");
    if (fechaLiquidacion) {
      formData.append("fechaLiquidacion", fechaLiquidacion);
    }

    return formData;
  };

  const handleAnalizarZip = async (event) => {
    event.preventDefault();
    if (!archivoZip) {
      Swal.fire("Conciliacion", "Selecciona el ZIP de liquidaciones Fiserv.", "info");
      return;
    }
    if (!fechaLiquidacion) {
      Swal.fire("Conciliacion", "Selecciona la fecha de liquidacion para buscar el SAS importado de ese dia.", "info");
      return;
    }

    try {
      setAnalizando(true);
      setResultadoClover(null);
      setResultadoConciliacion(null);
      const data = await importacionOperacionesApi.analizarZipFiserv(buildFormData(), token);
      setResultadoZip(data);
    } catch (error) {
      Swal.fire("Conciliacion", error.message || "No se pudo analizar el ZIP.", "error");
    } finally {
      setAnalizando(false);
    }
  };

  const handleSolicitarReportesClover = async () => {
    if (!reportesSolicitablesClover.length) {
      Swal.fire("Conciliacion", "No hay reportes Clover faltantes o con error para solicitar.", "info");
      return;
    }

    const comerciosUnicos = new Set(reportesSolicitablesClover.map((item) => item.merchantId).filter(Boolean)).size;
    const fechas = reportesSolicitablesClover
      .map((item) => String(item.date || "").slice(0, 10))
      .filter(Boolean)
      .sort();
    const rangoFechas = fechas.length
      ? `${formatDateOnly(fechas[0])}${fechas.length > 1 ? ` - ${formatDateOnly(fechas[fechas.length - 1])}` : ""}`
      : "-";

    const confirmacion = await Swal.fire({
      title: "Reportes Clover a solicitar",
      html: `
        <div style="text-align:left">
          <p><strong>Reportes a solicitar:</strong> ${reportesSolicitablesClover.length}</p>
          <p><strong>Comercios unicos:</strong> ${comerciosUnicos}</p>
          <p><strong>Fechas involucradas:</strong> ${rangoFechas}</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Obtener reportes",
      cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) {
      return;
    }

    try {
      setSolicitandoClover(true);
      const data = await importacionOperacionesApi.solicitarReportesClover({
        fechaLiquidacion,
        reportes: reportesSolicitablesClover,
      }, token);
      setResultadoClover(data);
      setPollingCobertura((data.solicitadas || 0) > 0 || (data.yaProcesando || 0) > 0);
      const icon = data.solicitadas > 0 ? "success" : "warning";
      const diferidasText = data.diferidas
        ? ` ${data.diferidas} quedaron para el proximo lote${data.maxSolicitudesAutoReportes ? ` (maximo local: ${data.maxSolicitudesAutoReportes})` : ""}.`
        : "";
      Swal.fire(
        "Pedido registrado",
        `${data.totalSolicitudes || 0} reportes evaluados. ${data.solicitadas || 0} registrados en auto-reportes. ${data.yaDisponibles || 0} ya disponibles. ${data.yaProcesando || 0} ya procesandose.${diferidasText}`,
        icon
      );
    } catch (error) {
      Swal.fire("Conciliacion", error.message || "No se pudieron pedir reportes Clover.", "error");
    } finally {
      setSolicitandoClover(false);
    }
  };

  const handleConciliarFiservClover = async () => {
    if (!fechaLiquidacion) {
      Swal.fire("Conciliacion", "Selecciona la fecha de liquidacion para conciliar contra el SAS importado.", "info");
      return;
    }
    if (!reportesDisponibles) {
      Swal.fire("Conciliacion", "No hay reportes Clover disponibles para conciliar operacion por operacion.", "info");
      return;
    }

    try {
      setConciliando(true);
      const data = await importacionOperacionesApi.conciliarFiservClover({ fechaLiquidacion }, token);
      setResultadoConciliacion(data);
      cargarHistorialConciliacion();
      setFiltroConciliacion("TODOS");
      setDetalleConciliacion(null);
    } catch (error) {
      Swal.fire("Conciliacion", error.message || "No se pudo conciliar Fiserv contra Clover.", "error");
    } finally {
      setConciliando(false);
    }
  };

  const abrirRunConciliacion = async (runId, filtros = filtrosDetalle) => {
    try {
      const payload = {
        estado: filtroConciliacion === "TODOS" ? "" : filtroConciliacion,
        terminal: filtros.terminal,
        comercio: filtros.comercio,
        fechaOperacion: filtros.fechaOperacion || null,
        importe: filtros.importe ? Number(filtros.importe) : null,
        tarjetaUltimos4: filtros.tarjetaUltimos4,
        autorizacion: filtros.autorizacion,
        merchantId: filtros.merchantId,
        tipoMedio: filtros.tipoMedio,
        estadoFinanciero: filtros.estadoFinanciero,
        confianzaClasificacion: filtros.confianzaClasificacion,
        soloPendientesRevision: filtros.soloPendientesRevision,
      };
      const data = await importacionOperacionesApi.obtenerRunConciliacion(runId, payload, token);
      setResultadoConciliacion(data);
      setDetalleConciliacion(null);
    } catch (error) {
      Swal.fire("Conciliacion", error.message || "No se pudo abrir la conciliacion historica.", "error");
    }
  };

  const aplicarFiltrosDetalle = () => {
    if (resultadoConciliacion?.run?.id) {
      abrirRunConciliacion(resultadoConciliacion.run.id);
    }
  };

  const revisarResultado = async (decision) => {
    if (!resultadoConciliacion?.run?.id || !detalleConciliacion?.id) {
      return;
    }
    const requiereCandidato = decision === "CONFIRMAR_MATCH"
      && !detalleConciliacion.cloverOperacionId
      && detalleConciliacion.candidatosClover?.length > 1;
    const { value: formValues, isConfirmed } = await Swal.fire({
      title: "Revision manual",
      html: `
        ${requiereCandidato ? `
          <label style="display:block;text-align:left;margin-bottom:4px">Candidato Clover</label>
          <select id="clover-candidate" class="swal2-input" style="width:100%">
            ${(detalleConciliacion.candidatosClover || []).map((item) => `<option value="${item.id}">${item.id} - ${formatCurrency(item.amount)} - ${item.cardLast4 || ""}</option>`).join("")}
          </select>
        ` : ""}
        <textarea id="manual-observation" class="swal2-textarea" placeholder="Observacion opcional"></textarea>
      `,
      showCancelButton: true,
      confirmButtonText: "Guardar revision",
      cancelButtonText: "Cancelar",
      preConfirm: () => ({
        observacion: document.getElementById("manual-observation")?.value || "",
        cloverOperacionId: document.getElementById("clover-candidate")?.value || detalleConciliacion.cloverOperacionId || "",
      }),
    });
    if (!isConfirmed) {
      return;
    }
    try {
      const data = await importacionOperacionesApi.revisarResultadoConciliacion(
        resultadoConciliacion.run.id,
        detalleConciliacion.id,
        { decision, ...formValues },
        token
      );
      setResultadoConciliacion(data);
      setDetalleConciliacion(null);
      cargarHistorialConciliacion();
    } catch (error) {
      Swal.fire("Conciliacion", error.message || "No se pudo guardar la revision manual.", "error");
    }
  };

  const recalcularRun = async (run) => {
    if (!run?.fechaLiquidacion) {
      return;
    }
    const confirmacion = await Swal.fire({
      title: "Recalcular conciliacion",
      text: "Se creara un run nuevo. El historico actual no se modifica.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Recalcular",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacion.isConfirmed) {
      return;
    }
    try {
      setConciliando(true);
      const data = await importacionOperacionesApi.conciliarFiservClover({
        fechaLiquidacion: String(run.fechaLiquidacion).slice(0, 10),
        recalcular: true,
        recalculoDeRunId: run.id,
      }, token);
      setResultadoConciliacion(data);
      cargarHistorialConciliacion();
    } catch (error) {
      Swal.fire("Conciliacion", error.message || "No se pudo recalcular.", "error");
    } finally {
      setConciliando(false);
    }
  };

  return (
    <section className={darkMode ? "container-dark importacion-operaciones-page" : "container-light bg-gris importacion-operaciones-page"}>
      <div className="container py-4">
        <TituloPagina title="Conciliacion de Operaciones" />

        <Alert variant="info" className="mt-4">
          Esta seccion nueva analiza liquidaciones Fiserv contra el universo Clover sin modificar la importacion actual.
        </Alert>

        <Card className="importacion-operaciones-card mt-3">
          <Card.Body>
            <div className="importacion-operaciones-toolbar">
              <div>
                <h5 className="mb-1">Clover / Fiserv</h5>
                <div className="text-muted small">
                  Primer analisis de ZIP Fiserv: detecta PDFs de debito y propone la ventana de compras Clover a cruzar.
                </div>
              </div>
              <Badge bg="secondary" className="importacion-operaciones-badge">MVP sin escritura</Badge>
            </div>

            <Form className="mt-3" onSubmit={handleAnalizarZip}>
              <div className="row g-3">
                <div className="col-md-2">
                  <Form.Group>
                    <Form.Label>Fecha de liquidacion</Form.Label>
                    <Form.Control type="date" value={fechaLiquidacion} onChange={(event) => setFechaLiquidacion(event.target.value)} />
                  </Form.Group>
                </div>
                <div className="col-md-2">
                  <Form.Group>
                    <Form.Label>Dias hacia atras</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="60"
                      value={diasBusqueda}
                      onChange={(event) => setDiasBusqueda(event.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>ZIP Fiserv</Form.Label>
                    <Form.Control type="file" accept=".zip" onChange={(event) => {
                      setArchivoZip(event.target.files?.[0] || null);
                      setResultadoZip(null);
                      setResultadoClover(null);
                      setResultadoConciliacion(null);
                    }} />
                  </Form.Group>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <Button type="submit" variant="success" className="w-100" disabled={!archivoZip || analizando}>
                    {analizando ? <><Spinner animation="border" size="sm" className="me-2" />Analizando...</> : "Analizar ZIP"}
                  </Button>
                </div>
              </div>
            </Form>
          </Card.Body>
        </Card>

        <Card className="importacion-operaciones-card mt-3">
          <Card.Body>
            <div className="importacion-operaciones-toolbar">
              <div>
                <h5 className="mb-1">HISTORIAL</h5>
                <div className="text-muted small">Conciliaciones persistidas. Abrir detalle usa el snapshot guardado.</div>
              </div>
              <Button variant="outline-secondary" size="sm" onClick={cargarHistorialConciliacion} disabled={cargandoHistorial}>
                {cargandoHistorial ? <><Spinner animation="border" size="sm" className="me-2" />Actualizando...</> : "Actualizar"}
              </Button>
            </div>
            {historialConciliacion?.metricas ? (
              <div className="importacion-operaciones-grid mt-3">
                <Card className="importacion-operaciones-card">
                  <Card.Body>
                    <div className="importacion-operaciones-kpi-label">Ultimas runs</div>
                    <div className="importacion-operaciones-kpi-value">{historialConciliacion.metricas.ultimasConciliaciones || 0}</div>
                  </Card.Body>
                </Card>
                <Card className="importacion-operaciones-card">
                  <Card.Body>
                    <div className="importacion-operaciones-kpi-label">Total operaciones</div>
                    <div className="importacion-operaciones-kpi-value">{historialConciliacion.metricas.totalOperaciones || 0}</div>
                  </Card.Body>
                </Card>
                <Card className="importacion-operaciones-card">
                  <Card.Body>
                    <div className="importacion-operaciones-kpi-label">MATCH auto</div>
                    <div className="importacion-operaciones-kpi-value">{formatPercent(historialConciliacion.metricas.matchAutomaticoPorcentaje)}</div>
                  </Card.Body>
                </Card>
                <Card className="importacion-operaciones-card">
                  <Card.Body>
                    <div className="importacion-operaciones-kpi-label">Probables</div>
                    <div className="importacion-operaciones-kpi-value">{formatPercent(historialConciliacion.metricas.probablesPorcentaje)}</div>
                  </Card.Body>
                </Card>
                <Card className="importacion-operaciones-card">
                  <Card.Body>
                    <div className="importacion-operaciones-kpi-label">Conflictos</div>
                    <div className="importacion-operaciones-kpi-value">{formatPercent(historialConciliacion.metricas.conflictosPorcentaje)}</div>
                  </Card.Body>
                </Card>
                <Card className="importacion-operaciones-card">
                  <Card.Body>
                    <div className="importacion-operaciones-kpi-label">Correcciones</div>
                    <div className="importacion-operaciones-kpi-value">{historialConciliacion.metricas.correccionesManuales || 0}</div>
                  </Card.Body>
                </Card>
              </div>
            ) : null}
            <div className="importacion-operaciones-table-wrap mt-3">
              <Table responsive hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Liquidacion</th>
                    <th>Ejecucion</th>
                    <th>Usuario</th>
                    <th>Fiserv</th>
                    <th>Clover</th>
                    <th>Match</th>
                    <th>Probables</th>
                    <th>Solo Fiserv</th>
                    <th>Solo Clover</th>
                    <th>Conflictos</th>
                    <th>Estado</th>
                    <th>Accion</th>
                  </tr>
                </thead>
                <tbody>
                  {historialConciliacion?.runs?.length ? historialConciliacion.runs.map((run) => (
                    <tr key={run.id}>
                      <td>{formatDateOnly(run.fechaLiquidacion)}</td>
                      <td>{run.fechaCreacion ? new Date(run.fechaCreacion).toLocaleString("es-AR") : "-"}</td>
                      <td>{run.usuarioNombre || "-"}</td>
                      <td>{run.cantidadFiserv}</td>
                      <td>{run.cantidadClover}</td>
                      <td>{run.cantidadMatch}</td>
                      <td>{run.cantidadMatchProbable}</td>
                      <td>{run.cantidadSoloFiserv}</td>
                      <td>{run.cantidadSoloClover}</td>
                      <td>{run.cantidadConflicto}</td>
                      <td><Badge bg={run.estado === "COMPLETED" ? "success" : run.estado === "COMPLETED_WITH_ISSUES" ? "warning" : "danger"}>{run.estado}</Badge></td>
                      <td>
                        <Button size="sm" variant="outline-success" onClick={() => abrirRunConciliacion(run.id)}>Ver detalle</Button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="12" className="importacion-operaciones-empty">
                        {historialConciliacion?.error || "Todavia no hay conciliaciones persistidas."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {resultadoZip ? (
          <>
            <div className="importacion-operaciones-grid mt-4">
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">PDFs debito</div>
                  <div className="importacion-operaciones-kpi-value">{resultadoZip.totalPdfsDebito ?? 0}</div>
                </Card.Body>
              </Card>
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">Leidos</div>
                  <div className="importacion-operaciones-kpi-value">{resultadoZip.totalPdfsLeidos ?? 0}</div>
                </Card.Body>
              </Card>
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">Con error</div>
                  <div className="importacion-operaciones-kpi-value">{resultadoZip.totalPdfsConError ?? 0}</div>
                </Card.Body>
              </Card>
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">Ventas detectadas</div>
                  <div className="importacion-operaciones-kpi-value">{resultadoZip.totalOperacionesDetectadas ?? 0}</div>
                </Card.Body>
              </Card>
              <Card className="importacion-operaciones-card">
                <Card.Body>
                  <div className="importacion-operaciones-kpi-label">Importe Fiserv</div>
                  <div className="importacion-operaciones-kpi-value">{formatCurrency(resultadoZip.totalImporteDetectado)}</div>
                </Card.Body>
              </Card>
            </div>

            <Alert variant="light" className="mt-3 mb-0">
              Para liquidacion {formatDateOnly(resultadoZip.fechaLiquidacion)}, Fiserv define que fechas de operacion hay que pedirle a Clover. Referencia inicial: desde {formatDateOnly(resultadoZip.ventanaComprasDesde)} hasta {formatDateOnly(resultadoZip.ventanaComprasHasta)}.
              {" "}Fuente de necesidades: {resultadoZip.fuenteNecesidades === "SAS_IMPORTACION" ? `SAS importado (${resultadoZip.sasImportacion?.nombreArchivo || "archivo detectado"})` : resultadoZip.fuenteNecesidades === "SAS_ARCHIVO" ? "Excel SAS manual" : "PDFs Fiserv"}.
            </Alert>

            {resultadoZip.errores?.length ? (
              <Alert variant="warning" className="mt-3 mb-0">
                {resultadoZip.errores.join(" ")}
              </Alert>
            ) : null}

            <Card className="importacion-operaciones-card mt-3">
              <Card.Body>
                <div className="importacion-operaciones-toolbar">
                  <div>
                    <h5 className="mb-1">Reportes Clover necesarios</h5>
                    <div className="text-muted small">
                      Esta lista sale de Fiserv y marca que fechas/comercios hay que buscar o pedirle a auto-reportes.
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      variant="outline-success"
                      disabled={conciliando || !reportesDisponibles}
                      onClick={handleConciliarFiservClover}
                    >
                      {conciliando ? <><Spinner animation="border" size="sm" className="me-2" />Conciliando...</> : "Conciliar Fiserv/Clover"}
                    </Button>
                    <Button
                      variant="success"
                      disabled={solicitandoClover || reportesSolicitablesClover.length === 0}
                      onClick={handleSolicitarReportesClover}
                    >
                      {solicitandoClover ? <><Spinner animation="border" size="sm" className="me-2" />Solicitando...</> : "Obtener reportes faltantes"}
                    </Button>
                  </div>
                </div>
                <Alert variant="warning" className="mt-3 mb-0">
                  Solo se solicitan reportes con mapping MATCH y cobertura FALTANTE. No se ejecutan DUDOSO, SIN_MAPEO ni Altas ambiguas.
                </Alert>

                <div className="importacion-operaciones-table-wrap mt-3">
                  <Table responsive hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Fecha operacion</th>
                        <th>Comercio/terminal Fiserv</th>
                        <th>Marca</th>
                        <th>Operaciones</th>
                        <th>Importe Fiserv</th>
                        <th>Estado</th>
                        <th>Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultadoZip.necesidadesClover?.length ? resultadoZip.necesidadesClover.map((item, index) => (
                        <tr key={`${item.fechaOperacion || "sin-fecha"}-${item.comercioFiserv}-${item.marca}-${index}`}>
                          <td>{formatDateOnly(item.fechaOperacion)}</td>
                          <td>{item.comercioFiserv}</td>
                          <td>{item.marca}</td>
                          <td>{item.cantidadOperacionesFiserv}</td>
                          <td>{formatCurrency(item.importeTotalFiserv)}</td>
                          <td>
                            <Badge bg={item.estado === "DATOS_INSUFICIENTES" ? "warning" : "info"}>{item.estado}</Badge>
                          </td>
                          <td>{item.motivo}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="7" className="importacion-operaciones-empty">Todavia no se pudieron inferir reportes Clover necesarios desde el ZIP.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {resultadoZip.diagnosticoMapeoClover ? (
              <Card className="importacion-operaciones-card mt-3">
                <Card.Body>
                  <div className="importacion-operaciones-toolbar">
                    <div>
                      <h5 className="mb-1">Diagnostico de mapeo Clover</h5>
                      <div className="text-muted small">
                        Necesidades unicas por terminal Fiserv y fecha de operacion, resueltas contra Altas.NDeTerminal, registry Clover y cobertura Mongo de auto-reportes.
                      </div>
                    </div>
                    <Badge bg="secondary">{resultadoZip.diagnosticoMapeoClover.resumen?.necesidadesClover || 0} necesidades</Badge>
                    {pollingCobertura ? <Badge bg="info" className="ms-2">actualizando cobertura</Badge> : null}
                  </div>

                  <div className="importacion-operaciones-grid mt-3">
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Necesidades totales</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoZip.diagnosticoMapeoClover.resumen?.necesidadesClover || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Mapping correcto</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoZip.diagnosticoMapeoClover.resumen?.match || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Reportes disponibles</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoZip.diagnosticoMapeoClover.resumen?.reportesDisponibles || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Reportes faltantes</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoZip.diagnosticoMapeoClover.resumen?.reportesFaltantes || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Con error</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoZip.diagnosticoMapeoClover.resumen?.reportesConError || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Procesando</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoZip.diagnosticoMapeoClover.resumen?.reportesProcesando || 0}</div>
                      </Card.Body>
                    </Card>
                  </div>

                  {metricasClasificacion ? (
                    <div className="importacion-operaciones-grid mt-3">
                      {[
                        ["Clover sin Fiserv", metricasClasificacion.cloverSinFiserv],
                        ["Credito confirmado", metricasClasificacion.creditoConfirmado],
                        ["Credito probable", metricasClasificacion.creditoProbable],
                        ["Debito pendiente", metricasClasificacion.debitoPendiente],
                        ["Debito liquidado post.", metricasClasificacion.debitoLiquidadoPosteriormente],
                        ["Devoluciones", metricasClasificacion.devoluciones],
                        ["Anulaciones", metricasClasificacion.anulaciones],
                        ["No clasificados", metricasClasificacion.noClasificados],
                      ].map(([label, value]) => (
                        <Card className="importacion-operaciones-card" key={label}>
                          <Card.Body>
                            <div className="importacion-operaciones-kpi-label">{label}</div>
                            <div className="importacion-operaciones-kpi-value">{value || 0}</div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  ) : null}

                  <div className="importacion-operaciones-table-wrap mt-3">
                    <Table responsive hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Terminal / Fiserv</th>
                          <th>Fecha</th>
                          <th>Nombre CRM</th>
                          <th>Candidato Clover</th>
                          <th>Merchant</th>
                          <th>Mapping</th>
                          <th>Cobertura</th>
                          <th>Operaciones Clover</th>
                          <th>Importe Clover</th>
                          <th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultadoZip.diagnosticoMapeoClover.items?.length ? resultadoZip.diagnosticoMapeoClover.items.map((item, index) => (
                          <tr key={`${item.terminalFiserv || "sin-terminal"}-${item.fechaOperacion || "sin-fecha"}-${index}`}>
                            <td>{item.terminalFiserv || "-"}</td>
                            <td>{formatDateOnly(item.fechaOperacion)}</td>
                            <td>{getNombreCrm(item.alta)}</td>
                            <td>{item.cloverCandidate?.commerceName || "-"}</td>
                            <td>{item.cloverCandidate?.dashboardMerchantId || "-"}</td>
                            <td>
                              <Badge bg={getMappingBadgeVariant(item.estado)}>{getMappingDisplayStatus(item.estado)}</Badge>
                              {item.confianza ? <span className="text-muted small ms-2">{item.confianza}%</span> : null}
                            </td>
                            <td>
                              <Badge bg={getCoverageBadgeVariant(item.coverage?.estadoUi)}>{item.coverage?.estadoUi || "NO_APLICA"}</Badge>
                            </td>
                            <td>{item.coverage?.operationsCount !== null && item.coverage?.operationsCount !== undefined ? item.coverage.operationsCount : "-"}</td>
                            <td>{item.coverage?.amount !== null && item.coverage?.amount !== undefined ? formatCurrency(item.coverage.amount) : "-"}</td>
                            <td>{item.coverage?.motivo || item.motivo}</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="10" className="importacion-operaciones-empty">Todavia no hay necesidades terminal + fecha para diagnosticar.</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <div className="importacion-operaciones-table-wrap mt-3">
                    <div className="importacion-operaciones-toolbar mb-2">
                      <div>
                        <h6 className="mb-1">Reportes Clover faltantes</h6>
                        <div className="text-muted small">
                          Insumo de Fase 4: merchant + fecha con mapping correcto y sin reporte disponible.
                        </div>
                      </div>
                      <Badge bg="warning">{reportesSolicitablesClover.length || 0} solicitables</Badge>
                    </div>
                    <Table responsive hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Comercio Clover</th>
                          <th>Merchant</th>
                          <th>Cuenta</th>
                          <th>Terminal Fiserv</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportesSolicitablesClover.length ? reportesSolicitablesClover.map((item, index) => (
                          <tr key={`${item.merchantId}-${item.date}-${index}`}>
                            <td>{formatDateOnly(item.date)}</td>
                            <td>{item.commerceName || "-"}</td>
                            <td>{item.merchantId || "-"}</td>
                            <td>{item.accountKey || "-"}</td>
                            <td>{item.terminalFiserv || "-"}</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="5" className="importacion-operaciones-empty">No hay reportes Clover faltantes o con error para los MATCH detectados.</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            ) : null}

            {resultadoConciliacion ? (
              <Card className="importacion-operaciones-card mt-3">
                <Card.Body>
                  <div className="importacion-operaciones-toolbar">
                    <div>
                      <h5 className="mb-1">RESULTADO DE CONCILIACION</h5>
                      <div className="text-muted small">
                        Run #{resultadoConciliacion.run?.id || "-"} - algoritmo {resultadoConciliacion.run?.versionAlgoritmo || "-"}.
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Badge bg="secondary">{resultadoConciliacion.resultados?.length || 0} filas</Badge>
                      {resultadoConciliacion.duplicadoAccidental ? <Badge bg="info">duplicado detectado</Badge> : null}
                      {resultadoConciliacion.run ? (
                        <Button size="sm" variant="outline-secondary" onClick={() => recalcularRun(resultadoConciliacion.run)} disabled={conciliando}>
                          Recalcular
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {resultadoConciliacion.advertencias?.length ? (
                    <Alert variant="warning" className="mt-3 mb-0">
                      {resultadoConciliacion.advertencias.join(" ")}
                    </Alert>
                  ) : null}
                  {resultadoConciliacion.reportesNoDisponibles?.length ? (
                    <Alert variant="info" className="mt-3 mb-0">
                      {resultadoConciliacion.reportesNoDisponibles.join(" ")}
                    </Alert>
                  ) : null}

                  <div className="importacion-operaciones-grid mt-3">
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Fiserv</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoConciliacion.resumen?.operacionesFiserv || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Match</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoConciliacion.resumen?.conciliadas || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Probables</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoConciliacion.resumen?.matchProbable || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Solo Fiserv</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoConciliacion.resumen?.soloFiserv || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Solo Clover</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoConciliacion.resumen?.soloClover || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Conflictos</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoConciliacion.resumen?.conflictos || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">MATCH auto %</div>
                        <div className="importacion-operaciones-kpi-value">{formatPercent(resultadoConciliacion.run?.matchAutomaticoPorcentaje)}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Potencial %</div>
                        <div className="importacion-operaciones-kpi-value">{formatPercent(resultadoConciliacion.run?.conciliacionPotencialPorcentaje)}</div>
                      </Card.Body>
                    </Card>
                  </div>

                  <div className="mt-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <ButtonGroup size="sm">
                      {conciliationFilters.map((status) => (
                        <Button
                          key={status}
                          variant={filtroConciliacion === status ? getConciliationBadgeVariant(status) : "outline-secondary"}
                          onClick={() => setFiltroConciliacion(status)}
                        >
                          {status}
                        </Button>
                      ))}
                    </ButtonGroup>
                    <div className="text-muted small">
                      Importe conciliado: {formatCurrency(resultadoConciliacion.resumen?.importeConciliado)}
                    </div>
                  </div>

                  <div className="row g-2 mt-2">
                    <div className="col-md-2"><Form.Control size="sm" placeholder="Terminal" value={filtrosDetalle.terminal} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, terminal: event.target.value }))} /></div>
                    <div className="col-md-2"><Form.Control size="sm" placeholder="Comercio" value={filtrosDetalle.comercio} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, comercio: event.target.value }))} /></div>
                    <div className="col-md-2"><Form.Control size="sm" type="date" value={filtrosDetalle.fechaOperacion} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, fechaOperacion: event.target.value }))} /></div>
                    <div className="col-md-2"><Form.Control size="sm" type="number" placeholder="Importe" value={filtrosDetalle.importe} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, importe: event.target.value }))} /></div>
                    <div className="col-md-1"><Form.Control size="sm" placeholder="Tarj." value={filtrosDetalle.tarjetaUltimos4} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, tarjetaUltimos4: event.target.value }))} /></div>
                    <div className="col-md-2"><Form.Control size="sm" placeholder="Autorizacion" value={filtrosDetalle.autorizacion} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, autorizacion: event.target.value }))} /></div>
                    <div className="col-md-2"><Form.Control size="sm" placeholder="Merchant" value={filtrosDetalle.merchantId} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, merchantId: event.target.value }))} /></div>
                    <div className="col-md-2">
                      <Form.Select size="sm" value={filtrosDetalle.tipoMedio} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, tipoMedio: event.target.value }))}>
                        <option value="">Tipo medio</option>
                        <option value="CREDITO">CREDITO</option>
                        <option value="DEBITO">DEBITO</option>
                        <option value="OTRO">OTRO</option>
                        <option value="DESCONOCIDO">DESCONOCIDO</option>
                      </Form.Select>
                    </div>
                    <div className="col-md-2">
                      <Form.Select size="sm" value={filtrosDetalle.estadoFinanciero} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, estadoFinanciero: event.target.value }))}>
                        <option value="">Estado financiero</option>
                        <option value="CONCILIADO">CONCILIADO</option>
                        <option value="PENDIENTE">PENDIENTE</option>
                        <option value="LIQUIDADO_POSTERIORMENTE">LIQUIDADO_POSTERIORMENTE</option>
                        <option value="DEVOLUCION">DEVOLUCION</option>
                        <option value="ANULADO">ANULADO</option>
                        <option value="DESCONOCIDO">DESCONOCIDO</option>
                      </Form.Select>
                    </div>
                    <div className="col-md-2">
                      <Form.Select size="sm" value={filtrosDetalle.confianzaClasificacion} onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, confianzaClasificacion: event.target.value }))}>
                        <option value="">Confianza</option>
                        <option value="CONFIRMADO">CONFIRMADO</option>
                        <option value="PROBABLE">PROBABLE</option>
                        <option value="DESCONOCIDO">DESCONOCIDO</option>
                      </Form.Select>
                    </div>
                    <div className="col-md-1 d-flex align-items-center">
                      <Form.Check
                        type="checkbox"
                        label="Pend."
                        checked={filtrosDetalle.soloPendientesRevision}
                        onChange={(event) => setFiltrosDetalle((prev) => ({ ...prev, soloPendientesRevision: event.target.checked }))}
                      />
                    </div>
                    <div className="col-md-2">
                      <Button size="sm" variant="outline-success" onClick={aplicarFiltrosDetalle} disabled={!resultadoConciliacion.run?.id}>Filtrar</Button>
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-lg-8">
                      <div className="importacion-operaciones-table-wrap">
                        <Table responsive hover size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>Estado</th>
                              <th>Fecha</th>
                              <th>Terminal</th>
                              <th>Importe</th>
                              <th>Tarjeta</th>
                              <th>Autorizacion</th>
                              <th>Tipo</th>
                              <th>Estado financiero</th>
                              <th>Confianza</th>
                              <th>Score</th>
                              <th>Evidencia</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultadosConciliacionFiltrados.length ? resultadosConciliacionFiltrados.map((item, index) => (
                              <tr
                                key={`${getResultStatus(item)}-${item.id || item.fiserv?.id || item.clover?.id || index}`}
                                onClick={() => setDetalleConciliacion(item)}
                                style={{ cursor: "pointer" }}
                              >
                                <td>
                                  <Badge bg={getConciliationBadgeVariant(getResultStatus(item))}>{getResultStatus(item)}</Badge>
                                  {getResultAutomaticStatus(item) !== getResultStatus(item) ? <div className="text-muted small">Auto: {getResultAutomaticStatus(item)}</div> : null}
                                </td>
                                <td>{formatDateOnly(item.fechaOperacion || item.fiserv?.operationDate || item.clover?.operationDate)}</td>
                                <td>{item.terminal || item.fiserv?.terminal || item.clover?.terminal || "-"}</td>
                                <td>{formatCurrency(item.importe ?? item.fiserv?.amount ?? item.clover?.amount)}</td>
                                <td>{item.tarjetaUltimos4 || item.fiserv?.cardLast4 || item.clover?.cardLast4 || "-"}</td>
                                <td>{item.autorizacion || item.fiserv?.authorizationCode || item.clover?.authorizationCode || "-"}</td>
                                <td><Badge bg={getClassificationBadgeVariant(getClassification(item).tipoMedioActual)}>{getClassification(item).tipoMedioActual || "-"}</Badge></td>
                                <td><Badge bg={getClassificationBadgeVariant(getClassification(item).estadoFinancieroActual)}>{getClassification(item).estadoFinancieroActual || "-"}</Badge></td>
                                <td>{getClassification(item).confianzaActual || "-"}</td>
                                <td>{item.score || 0}</td>
                                <td>{getClassification(item).motivoActual || item.motivo || item.reason || getEvidenceText(item.evidencia || item.evidence)}</td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan="11" className="importacion-operaciones-empty">No hay operaciones para el filtro seleccionado.</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <Card className="importacion-operaciones-card h-100">
                        <Card.Body>
                          <div className="importacion-operaciones-toolbar mb-2">
                            <h6 className="mb-0">Detalle</h6>
                            {detalleConciliacion ? <Badge bg={getConciliationBadgeVariant(getResultStatus(detalleConciliacion))}>{getResultStatus(detalleConciliacion)}</Badge> : null}
                          </div>
                          {detalleConciliacion ? (
                            <>
                              <div className="text-muted small mb-2">{getEvidenceText(detalleConciliacion.evidencia || detalleConciliacion.evidence)}</div>
                              {detalleConciliacion.clasificacion ? (
                                <Alert variant="secondary" className="py-2">
                                  <div className="d-flex gap-2 flex-wrap mb-1">
                                    <Badge bg={getClassificationBadgeVariant(detalleConciliacion.clasificacion.tipoMedioActual)}>{detalleConciliacion.clasificacion.tipoMedioActual}</Badge>
                                    <Badge bg={getClassificationBadgeVariant(detalleConciliacion.clasificacion.estadoFinancieroActual)}>{detalleConciliacion.clasificacion.estadoFinancieroActual}</Badge>
                                    <Badge bg="dark">{detalleConciliacion.clasificacion.confianzaActual}</Badge>
                                  </div>
                                  <div>{detalleConciliacion.clasificacion.motivoActual}</div>
                                  <div className="text-muted small mt-1">Clasificador {detalleConciliacion.clasificacion.versionClasificador}</div>
                                </Alert>
                              ) : null}
                              <Table size="sm" className="mb-0">
                                <thead>
                                  <tr>
                                    <th>Campo</th>
                                    <th>Fiserv</th>
                                    <th>Clover</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[
                                    ["Fecha", "operationDate"],
                                    ["Hora", "operationTime"],
                                    ["Importe", "amount"],
                                    ["Terminal", "terminal"],
                                    ["Tarjeta", "cardLast4"],
                                    ["Autorizacion", "authorizationCode"],
                                    ["Lote", "batchNumber"],
                                    ["Cupon/recibo", "receiptNumber"],
                                    ["Marca", "brand"],
                                    ["Cuotas", "installments"],
                                    ["Medio pago", "paymentMethod"],
                                    ["Resultado", "resultRaw"],
                                    ["Devoluciones", "refundCount"],
                                    ["Importe devolucion", "refundAmount"],
                                    ["Ingreso tarjeta", "cardEntryMethod"],
                                  ].map(([label, key]) => (
                                    <tr key={key}>
                                      <td>{label}</td>
                                      <td>{getOperationValue(detalleConciliacion.fiserv, key)}</td>
                                      <td>{getOperationValue(detalleConciliacion.clover, key)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                              {detalleConciliacion.candidatosClover?.length ? (
                                <div className="mt-3">
                                  <h6>Candidatos Clover</h6>
                                  <Table size="sm" className="mb-0">
                                    <tbody>
                                      {detalleConciliacion.candidatosClover.map((candidate) => (
                                        <tr key={candidate.id}>
                                          <td>{candidate.id}</td>
                                          <td>{formatCurrency(candidate.amount)}</td>
                                          <td>{candidate.cardLast4 || "-"}</td>
                                          <td>{candidate.authorizationCode || "-"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              ) : null}
                              {["MATCH_PROBABLE", "CONFLICTO"].includes(getResultAutomaticStatus(detalleConciliacion)) ? (
                                <div className="d-flex gap-2 flex-wrap mt-3">
                                  <Button size="sm" variant="success" onClick={() => revisarResultado("CONFIRMAR_MATCH")}>Confirmar match</Button>
                                  <Button size="sm" variant="warning" onClick={() => revisarResultado("RECHAZAR_MATCH")}>Rechazar match</Button>
                                  <Button size="sm" variant="outline-warning" onClick={() => revisarResultado("MARCAR_SIN_EQUIVALENCIA")}>Sin equivalencia</Button>
                                  {detalleConciliacion.decisionManual ? (
                                    <Button size="sm" variant="outline-secondary" onClick={() => revisarResultado("RESTAURAR_DECISION_AUTOMATICA")}>Restaurar auto</Button>
                                  ) : null}
                                </div>
                              ) : null}
                              {detalleConciliacion.decisionManual ? (
                                <Alert variant="info" className="mt-3 mb-0">
                                  {detalleConciliacion.decisionManual} por {detalleConciliacion.revisadoPorNombre || "-"} el {detalleConciliacion.fechaRevision ? new Date(detalleConciliacion.fechaRevision).toLocaleString("es-AR") : "-"}.
                                </Alert>
                              ) : null}
                            </>
                          ) : (
                            <div className="importacion-operaciones-empty">Selecciona una operacion para ver el cruce Fiserv/Clover.</div>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                  {resumenPorComercio.length ? (
                    <div className="importacion-operaciones-table-wrap mt-3">
                      <Table responsive hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Comercio</th>
                            <th>Merchant</th>
                            <th>Terminal</th>
                            <th>Clover total</th>
                            <th>Debito conciliado</th>
                            <th>Debito pendiente</th>
                            <th>Debito post.</th>
                            <th>Credito</th>
                            <th>Devoluciones</th>
                            <th>No clasificado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resumenPorComercio.map((item, index) => (
                            <tr key={`${item.merchantId || "merchant"}-${item.terminal || "terminal"}-${index}`}>
                              <td>{item.commerceName || "-"}</td>
                              <td>{item.merchantId || "-"}</td>
                              <td>{item.terminal || "-"}</td>
                              <td>{item.cloverTotal || 0}</td>
                              <td>{item.debitoConciliado || 0}</td>
                              <td>{item.debitoPendiente || 0}</td>
                              <td>{item.debitoLiquidadoPosteriormente || 0}</td>
                              <td>{item.credito || 0}</td>
                              <td>{item.devoluciones || 0}</td>
                              <td>{item.noClasificado || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : null}
                </Card.Body>
              </Card>
            ) : null}

            {resultadoClover ? (
              <Card className="importacion-operaciones-card mt-3">
                <Card.Body>
                  <div className="importacion-operaciones-toolbar">
                    <div>
                      <h5 className="mb-1">Pedidos enviados a auto-reportes</h5>
                      <div className="text-muted small">
                        Endpoint: {resultadoClover.autoReportesEndpoint}
                      </div>
                    </div>
                    <Badge bg="success">{resultadoClover.solicitadas || 0} iniciados</Badge>
                  </div>
                  <div className="importacion-operaciones-grid mt-3">
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Solicitados</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoClover.totalSolicitudes || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Iniciados</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoClover.solicitadas || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Ya disponibles</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoClover.yaDisponibles || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Ya procesando</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoClover.yaProcesando || 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="importacion-operaciones-card">
                      <Card.Body>
                        <div className="importacion-operaciones-kpi-label">Con error</div>
                        <div className="importacion-operaciones-kpi-value">{resultadoClover.conError || 0}</div>
                      </Card.Body>
                    </Card>
                  </div>
                  <div className="importacion-operaciones-table-wrap mt-3">
                    <Table responsive hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Fecha operacion</th>
                          <th>Comercio Fiserv</th>
                          <th>Comercio Clover</th>
                          <th>Cuenta</th>
                          <th>Estado</th>
                          <th>Job</th>
                          <th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultadoClover.resultados?.map((item, index) => (
                          <tr key={`${item.fechaOperacion || "sin-fecha"}-${item.comercioFiserv}-${index}`}>
                            <td>{formatDateOnly(item.fechaOperacion)}</td>
                            <td>{item.comercioFiserv || item.merchantId || "-"}</td>
                            <td>{item.comercioClover || "-"}</td>
                            <td>{item.accountKey || "-"}</td>
                            <td>
                              <Badge bg={item.estado === "QUEUED" ? "success" : item.estado === "SKIPPED_COMPLETE" || item.estado === "SKIPPED_RUNNING" ? "info" : "danger"}>{item.estado}</Badge>
                            </td>
                            <td>{item.jobId || "-"}</td>
                            <td>{item.motivo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            ) : null}

            <Card className="importacion-operaciones-card mt-3 mb-5">
              <Card.Body>
                <h5 className="mb-3">Detalle PDFs Fiserv</h5>
                <div className="importacion-operaciones-table-wrap">
                  <Table responsive hover size="sm" className="mb-0 importacion-operaciones-fiserv-table">
                    <thead>
                      <tr>
                        <th>Archivo</th>
                        <th>Marca</th>
                        <th>Fecha operacion</th>
                        <th>Fecha archivo</th>
                        <th>Comercio</th>
                        <th>Ventas</th>
                        <th>Importe</th>
                        <th>Terminal</th>
                        <th>Lote</th>
                        <th>Cupon</th>
                        <th>Tarjeta</th>
                        <th>Cuotas</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultadoZip.items?.length ? resultadoZip.items.map((item, index) => (
                        <tr key={`${item.nombreArchivo}-${index}`}>
                          <td className="importacion-operaciones-col-archivo">{item.nombreArchivo}</td>
                          <td>{item.marca}</td>
                          <td>{formatDateOnly(item.fechaOperacionDetectada)}</td>
                          <td>{formatDateOnly(item.fechaLiquidacionDetectada)}</td>
                          <td>{item.comercioFiservDetectado || "-"}</td>
                          <td>{item.operacionesDetectadas ?? 0}</td>
                          <td>{item.importeDetectado ? formatCurrency(item.importeDetectado) : "-"}</td>
                          <td>{formatListPreview(item.terminalesDetectadas)}</td>
                          <td>{formatListPreview(item.lotesDetectados)}</td>
                          <td>{formatListPreview(item.cuponesDetectados)}</td>
                          <td>{formatListPreview(item.tarjetasDetectadas)}</td>
                          <td>{formatListPreview(item.cuotasDetectadas)}</td>
                          <td>
                            <Badge bg={item.error ? "danger" : "success"}>{item.error ? "ERROR" : "LEIDO"}</Badge>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="13" className="importacion-operaciones-empty">No se detectaron PDFs de debito en el ZIP.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        ) : null}
      </div>

      <Footer />
    </section>
  );
};

export default ConciliacionOperaciones;
